import { ReactElement, useState } from 'react';

import styles from './my-loyalty-history-remake.module.scss';
import { useTranslation } from 'next-i18next';
import { useDashboard } from '@utmb/contexts/DashboardContext';
import { MyTable } from '@utmb/design-system/my-table/MyTable';
import { MyTableHeader } from '@utmb/design-system/my-table/MyTableHeader';
import { MyTableCell } from '@utmb/design-system/my-table/MyTableCell';
import { MyTableRow } from '@utmb/design-system/my-table/MyTableRow';
import { LoyaltyAllActionEnum } from '@utmb/types/enum/LoyaltyAllActionEnum';
import { IconsEnum } from '@utmb/types/icons';
import { Font } from '@utmb/design-system/text/font';
import { Icon } from '@utmb/design-system/icons/Icon';
import { getColorStyle, getLoyaltyColor } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import MyUtmbPoints from '@utmb/design-system/my-utmb-points/MyUtmbPoints';
import { LoyaltyLevelEnum } from '@utmb/types/enum/LoyaltyLevelEnum';
import { ButtonRemake } from '@utmb/design-system/button-remake/ButtonRemake';
import { PointsSummary } from '../my-utmb/points-summary/points-summary';

const LIMIT = 7;

export function MyLoyaltyHistoryRemake(): ReactElement {
    const { t } = useTranslation('myutmb');
    const { dashboardMember } = useDashboard();
    const [displayMore, setDisplayMore] = useState<boolean>(false);

    function getActionIcon(action: LoyaltyAllActionEnum) {
        if (action === LoyaltyAllActionEnum.CONTEST_WINNER) {
            return IconsEnum.GAME;
        } else if (action === LoyaltyAllActionEnum.DONATE_LEVEL1) {
            return IconsEnum.DONATION;
        } else if (action === LoyaltyAllActionEnum.DONATE_LEVEL2) {
            return IconsEnum.DONATION;
        } else if (action === LoyaltyAllActionEnum.DONATE_LEVEL3) {
            return IconsEnum.DONATION;
        } else if (action === LoyaltyAllActionEnum.FINISH_RACE) {
            return IconsEnum.FINISH_INDEX;
        } else if (action === LoyaltyAllActionEnum.FINISH_RACE_WS) {
            return IconsEnum.FINISH_INDEX;
        } else if (action === LoyaltyAllActionEnum.INIT) {
            return IconsEnum.OTHER;
        } else if (action === LoyaltyAllActionEnum.MOBILITY_TICKET) {
            return IconsEnum.MOBILITY;
        } else if (action === LoyaltyAllActionEnum.OTHER) {
            return IconsEnum.OTHER;
        } else if (action === LoyaltyAllActionEnum.PARTICIPATE_RACE) {
            return IconsEnum.BIB;
        } else if (action === LoyaltyAllActionEnum.PARTICIPATE_RACE_WS) {
            return IconsEnum.BIB;
        } else if (action === LoyaltyAllActionEnum.SOLIDARITY_BIB) {
            return IconsEnum.CHARITY_BIB;
        } else if (action === LoyaltyAllActionEnum.VOLUNTEER) {
            return IconsEnum.VOLONTEER;
        } else {
            return IconsEnum.OTHER;
        }
    }

    const activeItems = dashboardMember?.loyalty?.history ? dashboardMember.loyalty.history.filter((h) => h.active) : [];
    const inactiveItems = dashboardMember?.loyalty?.history ? dashboardMember.loyalty.history.filter((h) => !h.active).slice(0, 3) : [];
    const filteredHistory = [...activeItems, ...inactiveItems];

    return (
        <div className={styles.myLoyaltyHistoryRemake}>
            {dashboardMember?.loyalty ? (
                <>
                    <PointsSummary value={dashboardMember.loyalty.points} />

                    <MyTable className={styles.table}>
                        <MyTableHeader className={styles.table_header}>
                            <MyTableCell column={3}>{t('myUtmb.loyalty.history.actionLabel')}</MyTableCell>
                            <MyTableCell hiddenOnDesktop column={3}>
                                {t('myUtmb.loyalty.history.detailLabel')}
                            </MyTableCell>
                            <MyTableCell hiddenOnMobile column={1}>
                                {t('myUtmb.loyalty.history.dateLabel')}
                            </MyTableCell>
                            <MyTableCell column={1}>{t('myUtmb.loyalty.history.pointsLabel')}</MyTableCell>
                        </MyTableHeader>
                        {filteredHistory.slice(0, displayMore ? filteredHistory.length : LIMIT).map((entry, idx) => (
                            <MyTableRow
                                key={`${entry.action}-${entry.dateIso}-${idx}`}
                                className={[styles.customTableRow, entry.active ? undefined : styles.tableRowDisabled].filter(Boolean).join(' ')}
                                height={50}
                                showDetailsClassName={styles.customShowDetailTableRow}
                            >
                                <MyTableCell column={3}>
                                    <div className={styles.wrapAction}>
                                        <div className={styles.wrapIcon}>
                                            <Icon
                                                variant={getActionIcon(entry.action)}
                                                width={50}
                                                height={50}
                                                color={getColorStyle(entry.active ? ColorTypesEnum.PRIMARY : ColorTypesEnum.BLUE_LIGHT)}
                                            />
                                        </div>
                                        <Font.OxaniumBold desktop="14" mobile="14">
                                            {t(`myUtmb.loyalty.history.action-${entry.action}`)}
                                        </Font.OxaniumBold>
                                    </div>
                                </MyTableCell>
                                <MyTableCell
                                    className={styles.responsiveCell + ' ' + styles.responsiveDesktopCell + ' ' + styles.labelCell}
                                    hiddenOnDesktop
                                    column={3}
                                >
                                    <Font.OxaniumRegular desktop="14" mobile="14">
                                        {entry.label}
                                    </Font.OxaniumRegular>
                                </MyTableCell>
                                <MyTableCell className={styles.responsiveCell + ' ' + styles.dateCell} hiddenOnMobile column={1}>
                                    <Font.OxaniumRegular desktop="14" mobile="14">
                                        {entry.dateIso}
                                    </Font.OxaniumRegular>
                                </MyTableCell>
                                <MyTableCell column={1}>
                                    <div className={styles.wrapPoints}>
                                        <MyUtmbPoints
                                            color={getLoyaltyColor(dashboardMember.loyalty?.currentLevel ?? LoyaltyLevelEnum.LEVEL0)}
                                            quantity={entry.points}
                                        />
                                    </div>
                                </MyTableCell>
                            </MyTableRow>
                        ))}
                    </MyTable>

                    {filteredHistory.length > LIMIT ? (
                        <ButtonRemake
                            customClassName={styles.moreButton}
                            label={displayMore ? t('myutmb:myUtmb.myStones.displayLess') : t('myutmb:myUtmb.myStones.displayMore')}
                            action={() => setDisplayMore(!displayMore)}
                            variant="solid"
                        />
                    ) : null}
                </>
            ) : null}
        </div>
    );
}
